import { useIsFocused } from "expo-router/react-navigation";
import React, { useCallback, useMemo, useRef, useState } from "react";

import PlayerDetailView from "./PlayerDetailView";
import { PlayerDetailContainerProps } from "@/types/playerDetailTypes";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";

import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Container component for player detail page with watchlist management
 * @param props - Props containing player data, loading state, and event handlers
 */
const PlayerDetailContainer = (props: PlayerDetailContainerProps) => {
  const [showActionSheet, setShowActionSheet] = useState<boolean>(false);
  const { resolvedTheme } = useTheme();

  // This master-detail pane lives on the roster-management / watchlist LIST
  // screens, which stay mounted UNDER a pushed `/players/{id}` detail route.
  // When this screen is not focused, that dedicated route's PlayerDetailCenter
  // is the canonical PlayerDetailGetPlayer owner, so pause our duplicate fetch
  // (SE-5038 AC2). When focused (desktop inline master-detail, no route push),
  // the pane stays the owner and fetches once.
  const isFocused = useIsFocused();

  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);

  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as React.RefObject<CustomPromptRefProps>;
  const { handleAddWatchlistPress } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const handleAddPlayer = useCallback(() => setShowActionSheet(true), []);
  const handleClose = useCallback(() => setShowActionSheet(false), []);

  return (
    <PlayerDetailView
      showActionSheet={showActionSheet}
      onAddPlayer={handleAddPlayer}
      onClose={handleClose}
      promptRef={promptRef}
      onAddWatchlistPress={handleAddWatchlistPress}
      isDark={isDark}
      pauseDetailQuery={!isFocused}
      {...props}
    />
  );
};

export default PlayerDetailContainer;
